/**
 * The `app` namespace, in English — everything a signed-in person reads.
 *
 * It exists at all because of the session guard: "checking your session" and the stub home
 * screen are post-auth copy, and putting them in `entrance` would mean every language pack
 * has to ship the entrance and the application together. Namespaces are split by screen
 * family so a translator can finish one family at a time (see `lib/i18n/catalog.ts`).
 */

export const appEn = {
  "session.checking": "Checking your session…",
  // The same wait on `/`, whose signed-out answer is the public landing page. "Checking your
  // session" is the wrong sentence for somebody who has never had one — it reads as a gate,
  // and a first-time visitor should not meet a gate before the page.
  "session.loading": "Loading…",

  "home.title": "Signed in",
  "home.greeting": "Signed in as {name}.",
  "home.placeholder": "The team and project screens are not built yet.",
  "home.workspaceUnclaimed": "Your own company has not been set up yet.",
  "home.action.setUpCompany": "Set up your company",
  "home.action.signOut": "Sign out",

  // The application chrome (KLQ-20260819-003). It sits above every signed-in screen, so its
  // copy is `app` and not the screen family's — a project screen and a drawing screen read
  // the same rail.
  // "Studio", not "Workspace": §6.6 allows the internal word only on the settings screen,
  // and this is the rail's accessible name — read aloud on every screen there is. SK and DE
  // have said ateliér/Büro here since they were written.
  "chrome.nav.label": "Studio",
  "chrome.nav.open": "Open the menu",
  "chrome.nav.close": "Close the menu",
  // The root task page (`MCK-20260521-009`), first in the rail since 2026-09-17. Also the crumb
  // on `/tasks`, so the two agree.
  "chrome.nav.tasks": "My tasks",
  "chrome.nav.projects": "Projects",
  "chrome.nav.people": "People",
  "chrome.nav.firms": "Firms",
  "chrome.nav.settings": "Studio settings",

  // The avatar menu's own two entries. `home.action.signOut` is reused rather than
  // duplicated — it is the same act, said the same way, in the two places it is offered.
  "menu.profile": "Your profile",
  // The client roadmap (`KLQ-20260904-003`). "What we are building" and not "Roadmap":
  // the menu says what the page answers, and the reader is a customer, not a product manager.
  "menu.roadmap": "What we are building",

  // The legal documents, linked from the avatar menu (`KLQ-20260814-007`). One click from
  // every signed-in page; the document is the second click.
  "legal.heading": "Legal documents",
  "legal.imprint": "Imprint",
  "legal.terms": "Terms of service",
  "legal.privacy": "Privacy policy",
  "legal.avv": "Order processing agreement",
  "legal.subprocessors": "Subprocessors",

  // The workspace switcher on the avatar (`KLQ-20260819-007` §6). Only drawn for somebody who
  // works for more than one studio, which is why none of this copy explains what a workspace
  // is: the people who see it already have two.
  "switcher.label": "Working as {name}",
  "switcher.heading": "Your studios",
  "switcher.role.admin": "Admin",
  "switcher.role.manager": "Manager",
  "switcher.role.member": "Member",
  "switcher.roleAndHome": "{role} · your own studio",
  "chrome.nav.inbox": "Inbox",
  "chrome.nav.recent": "Recent",
  "chrome.nav.search": "Search",
  "chrome.nav.notifications": "Notifications",
  // Said on every drawn-but-dead control. One sentence, so a person who reads it once on the
  // rail recognises it on the top bar rather than parsing a second wording.
  "chrome.unavailable": "Not available yet",
  "chrome.breadcrumb": "Where you are",
  "chrome.searchShortcut": "⌘K",

  // The project tab row (KLQ-20260819-004). Same namespace as the rail for the same reason:
  // the row is chrome, drawn above whichever project screen is current, and every later wave
  // lights one of these up without touching its copy.
  "tabs.label": "Project sections",
  "tabs.overview": "Overview",
  "tabs.drawings": "Documentation",
  "tabs.register": "Register",
  "tabs.transmittals": "Transmittals",
  "tabs.reviews": "Reviews",
  "tabs.tasks": "Tasks",
  "tabs.activity": "Activity",
  "tabs.people": "People",
  "tabs.settings": "Settings",

  // The project shell's own two states.
  "project.missing.title": "No such project",
  // Deliberately one sentence for two causes. The backend answers 404 both for a project
  // that does not exist and for one this caller has no relationship to, precisely so the
  // second is indistinguishable from the first (MCK-20260521-004 invariant 4) — copy that
  // said "or you may not have access" would undo that in the browser.
  "project.missing.body":
    "This project does not exist, or the link is out of date.",
  "project.empty.title": "Nothing here yet",
  "project.empty.body":
    "The screens for your role here are not built yet. Your access is fine; there is nothing to show.",
  "project.action.backToProjects": "Back to projects",

  // The two shared containers (`KLQ-20260824-001`). Their copy is `app` and not a screen
  // family's for the chrome's reason: the tree table and the filter rail are drawn above
  // whichever screen mounts them, and every word *about* the data comes from that screen as
  // a prop. What is left is these five, which are about the control itself.
  "treeTable.expand": "Expand {name}",
  "treeTable.collapse": "Collapse {name}",
  "treeTable.selectRow": "Select {name}",
  "treeTable.selectAll": "Select all rows",
  "treeTable.editCount": "{count, plural, one {# row} other {# rows}}",
  // The second number (`KLQ-20260828-003` §F.2). A write count is the honest number for a
  // *write* and a misleading number for an *effect*: naming a responsible on three buildings
  // writes three rows and changes what four hundred display. So the editor states both when
  // they differ, and this one is deliberately worded as *display* — nothing beneath is
  // written, and a person who read "412 rows" would expect an undo to have 412 things in it.
  "treeTable.editReach":
    "{count, plural, one {# row beneath shows it} other {# rows beneath show it}}",
  // What a `Delete` over a large selection asks before it writes. "Values", not "rows":
  // the gesture empties cells, and a person who read "delete 40 rows" would be right to be
  // frightened of a button that does nothing of the sort.
  "treeTable.clearAsk":
    "{count, plural, one {Clear the values in # row?} other {Clear the values in # rows?}}",
  "treeTable.undo": "Undo (Cmd+Z)",
  "treeTable.redo": "Redo (Shift+Cmd+Z)",
  "treeTable.editApply": "Apply",
  "treeTable.editCancel": "Cancel",
  // The table's own two menu actions (`KLQ-20260824-002`). Chrome words, so they live here
  // with expand and select rather than in every register that draws a right-click.
  "treeTable.copy": "Copy",
  "treeTable.paste": "Paste",
  // The picker a cell opens (`KLQ-20260828-001` §B.3). The list itself owns no words —
  // `ListboxPopup` takes every one of them as a prop, so these three are the grid's.
  "treeTable.pickerSearch": "Search",
  "treeTable.pickerEmpty": "Nothing matches",
  "treeTable.pickerNone": "— none —",

  // Importing a sheet into a table (`KLQ-20260824-002`). The words are `app` for the reason
  // the tree table's own are: the drawer is drawn above whichever screen mounts it, and every
  // word *about* the data — the field labels, the verdict reasons — comes from that screen's
  // adapter rather than from here.
  "import.title": "Import from a sheet",
  "import.close": "Close",
  "import.cancel": "Cancel",
  "import.done": "Done",
  "import.apply":
    "{count, plural, =0 {Import} one {Import # row} other {Import # rows}}",
  "import.written":
    "{count, plural, =0 {Nothing was written.} one {# row imported.} other {# rows imported.}}",
  "import.writeFailed":
    "{count, plural, one {# row was refused.} other {# rows were refused.}}",
  "import.failed": "The import could not be completed.",
  "import.headerRow": "The first row names the columns",
  "import.mapping": "Columns",
  "import.ignore": "— not imported —",
  "import.column": "Column {number}",
  "import.required": "Choose which column holds: {field}.",
  "import.checking": "Checking what this would do…",
  "import.colRow": "Row",
  "import.colWhat": "What",
  "import.colWhere": "Where",
  "import.colResult": "Result",
  "import.include": "Import row {number}",
  "import.verdict.create": "New",
  "import.verdict.update": "Update",
  "import.verdict.skip": "Already there",
  "import.verdict.error": "Cannot import",
  // Pasting into a table that has no import adapter. Said out loud rather than swallowed: a
  // keystroke that does nothing at all is indistinguishable from a broken table.
  "import.unsupported": "Importing into this table is not supported yet.",

  "slicer.facet": "Slice by",
  "slicer.search": "Search",
  "slicer.noMatches": "Nothing matches that search.",
  // The select column (owner decision #86, 2026-09-15). The rail's own words and not the
  // table's `treeTable.selectRow`: in German and Slovak the slice is already *Auswahl* /
  // *výber* (`docs/domain/ui-vocabulary.md`), so a tick said with the same word would put the
  // two things the decision separated back under one name. Ticking is *markieren* / *označiť*.
  "slicer.tick": "Select {name}",
  "slicer.ticked": "{count, plural, one {# selected} other {# selected}}",
  "slicer.sliceTicked": "Show only these",
  "slicer.clearTicks": "Clear selection",

  // The phone form of the rail (`KLQ-20260905-002`): one chip that opens the rail in a
  // `Drawer`. The chip says what the slice *is* rather than what the control does — the name of
  // the one row the list is narrowed to is the answer a person came for, where a bare "Slice"
  // is a control nobody has touched yet. Three cases because no single name is true for all
  // three: nothing sliced, one row, several.
  "slicer.open": "{axis}: everything",
  "slicer.openOne": "{axis}: {name}",
  "slicer.openMany": "{axis}: {count} rows",
  "slicer.close": "Close the axis",

  // The `⋯` trigger's accessible name, in the same family and for the same reason: the
  // control is chrome, every word *inside* one of its menus comes from the screen that
  // mounts it. One key, so a screen reader hears the same phrase on every row of the
  // product rather than four attempts at it.
  "menu.more": "More actions",

  // The re-acceptance notice (`KLQ-20260814-007` §C). `app` and not `legal`: the `legal`
  // namespace is the chrome around the public document pages, and this is signed-in chrome,
  // drawn above whichever screen is current.
  //
  // The copy is a notice and never a warning, because the product is not withholding anything
  // the person already has. It says what changed and what is unaffected, in that order, so
  // somebody who reads one sentence reads the reassuring one.
  "reacceptance.title": "The documents you agreed to have changed",
  "reacceptance.body":
    "You can keep working, read everything already here and export it. Agreeing is only needed before you start something new.",
  // A name per kind. Never the document's own title: that text is counsel's and lives in the
  // file they wrote, and a catalogue is the wrong place for a second copy of it.
  // TWIN: `DOCUMENT_NAME_KEYS` in `features/legal/ReAcceptanceNotice.tsx`.
  "reacceptance.document.terms": "Terms of service",
  "reacceptance.document.privacy": "Privacy policy",
  "reacceptance.document.avv": "Order processing agreement",
  "reacceptance.version": "Version {version}",
  "reacceptance.read": "Read the new version",
  // "Agree" and not "Accept": the button records consent to a text, and the row beside it
  // already says which one.
  "reacceptance.accept": "Agree",
  "reacceptance.unavailable": "This document could not be loaded. Try again in a moment.",
  "reacceptance.failed": "That could not be recorded. Try again.",
  // The `409`. Said plainly, because the refusal is the feature: the server would rather
  // record nothing than record that somebody agreed to text they never saw.
  "reacceptance.stale":
    "The text changed while this page was open. Read the current version, then agree.",
  // --- Exports (`MCK-20260812-005`) ---------------------------------------------------
  //
  // In `app` rather than a namespace of its own: the workspace list is signed-in chrome
  // reached from the avatar menu and from an email, and the project panel is the same list
  // asked about one project. One family of copy, one place.
  //
  // Nothing here names a tier, a plan or an entitlement, and nothing may. Export is free in
  // every tier and every licence state (`MCK-20260521-001`), so a sentence hinting otherwise
  // would be false as well as unkind.
  "exports.project.title": "Export this project",
  "exports.project.lede":
    "A single ZIP with every drawing and version, the tasks, the comments and the audit log, plus the same information as JSON and CSV. Nothing is left out.",
  "exports.workspace.title": "Exports",
  "exports.workspace.lede":
    "Every export this studio has run, its own and each project’s. Exports are always visible to admins.",
  "exports.start": "Export this project",
  "exports.startWorkspace": "Export everything",
  "exports.download": "Download",
  "exports.loading": "Loading exports…",
  "exports.explains": "An export is a downloadable bundle of drawings and records, prepared in the background.",
  "exports.empty.project.title": "No exports yet",
  "exports.empty.project": "Nothing has been exported from this project yet.",
  "exports.empty.workspace.title": "No exports yet",
  "exports.empty.workspace": "Nothing has been exported from this studio yet.",
  // The refusal, said as what is true rather than as a permission the person lacks.
  "exports.notPermitted": "An admin exports the whole studio. A project export needs no admin.",
  "exports.expires": "The file is deleted on {when}.",
  "exports.progress.label": "Export progress",
  "exports.scope.project": "Project",
  "exports.scope.workspace": "Whole studio",
  // The phases, in words rather than in the column's own vocabulary. "Collecting" and
  // "Packing" are what is happening; `collecting_metadata` is what the row says.
  "exports.phase.queued": "Waiting",
  "exports.phase.collecting": "Collecting",
  "exports.phase.writing": "Packing",
  "exports.phase.finalising": "Finishing",
  "exports.phase.ready": "Ready",
  "exports.phase.failed": "Failed",
  "exports.phase.expired": "Deleted",

  // Canon components (`KLQ-20261024-001` §B): the fixed words `DropZone`, `InlineSuggestion`
  // and `PersonLabel` own. Everything else they print is the caller's.
  "dropZone.over": "Drop them and we’ll prepare the versions",
  "inlineSuggestion.dismiss": "Not now",
  "person.self": "(you)",
  "person.erased": "Former member",
} as const;

export type AppMessageKey = keyof typeof appEn;
